import type { LayoutRectangle } from 'react-native';
import Animated, {
    Extrapolation,
    interpolate,
    type SharedValue,
    useAnimatedStyle,
} from 'react-native-reanimated';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    XStack,
    YStack,
} from 'smoothie';

import LeftFlower from '../../../assets/images/illustrations/bee-flower-left.png';
import RightFlower from '../../../assets/images/illustrations/bee-flower-right.png';
import { useComponentLayout } from '../../common/hooks/useComponentLayout';
import type { Guide } from '../../guidance/types/Guide';
import { useShowGuideMessaging } from '../hooks/useShowGuideMessaging';

const getTransformDefinition = (
    scrollOffsetValue: number | undefined,
    layout: LayoutRectangle | undefined,
    leftOrRight: 'left' | 'right',
) => {
    'worklet'; // Required for a synchronous function to be called in the reanimated worklet

    const defaultDimensions = { width: 125, height: 194 };
    if (scrollOffsetValue === undefined || layout === undefined) {
        scrollOffsetValue = 0;
        layout = { y: 0, x: 0, ...defaultDimensions };
    }

    const direction = leftOrRight === 'left' ? 1 : -1;
    const inputRange = [
        layout.y - 600,
        layout.y - 300,
        layout.y + layout.height - 100,
    ];
    return {
        transform: [
            {
                translateX: interpolate(
                    scrollOffsetValue,
                    inputRange,
                    [-20 * direction, 20 * direction, 30 * direction],
                    Extrapolation.CLAMP,
                ),
            },
            {
                scale: interpolate(
                    scrollOffsetValue,
                    inputRange,
                    [0.6, 0.9, 1],
                    Extrapolation.CLAMP,
                ),
            },
        ],
        ...defaultDimensions,
    };
};

interface MessagingGetStartedCtaProps {
    guide?: Guide | null;
    scrollOffset: SharedValue<number>; // If needed, we could make this optional to render the component statically
}

const MessagingGetStartedCta = ({
    guide,
    scrollOffset,
}: MessagingGetStartedCtaProps) => {
    const showGuideMessagingModal = useShowGuideMessaging();
    const [layout, onLayout] = useComponentLayout();

    const leftImageAnimatedStyle = useAnimatedStyle(() => {
        return getTransformDefinition(scrollOffset?.value, layout, 'left');
    });
    const rightImageAnimatedStyle = useAnimatedStyle(() => {
        return getTransformDefinition(scrollOffset?.value, layout, 'right');
    });

    return (
        <YStack
            alignItems="center"
            onPress={() => {
                showGuideMessagingModal();
            }}
            onLayout={onLayout}
            width="100%"
            maxWidth={400}
            justifyContent="center"
            alignSelf="center"
            paddingHorizontal={0}
            $md={{ paddingHorizontal: 20 }}
        >
            <XStack>
                <Animated.Image
                    source={LeftFlower}
                    style={leftImageAnimatedStyle}
                />
                <Animated.Image
                    source={RightFlower}
                    style={rightImageAnimatedStyle}
                />
            </XStack>

            <BodyMediumRegular
                width={280}
                marginTop={12}
                marginBottom={32}
                textAlign="center"
            >
                This is where your latest messages from your Guide appear
            </BodyMediumRegular>

            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SMALL}
                text={`Message ${guide?.firstName ?? 'your Guide'}`}
                onPress={() => {
                    showGuideMessagingModal();
                }}
            />
        </YStack>
    );
};

export default MessagingGetStartedCta;
